import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from 'react-router-dom';
import * as Steps from './steps/index';
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import {
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  getBasicInfo,
  SESSION_STORAGE_BASIC_INFO_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY,
} from '../../../sessionStorage';
import { saveSessionEvent } from "../../../session";
import CheckSSOLogin from '../Common/CheckSSOLogin';
import './FinalExpense.scss';
import { EasyStepsSchedule, EasyStepsConfirmation } from "./Helpers/Constant";
import PropTypes from 'prop-types';
import { InsurancePlan } from "../FinalExpense/Helpers/Constant";
import { ChoiceConvenienceData } from "../Common/Appointment/CustomAppointmentData";

export default function FinalExpense(props) {

  useEffect(() => {
    saveSessionEvent();
  }, [props.location]);

  const path = props.location ? props.location.pathname.split("/").pop() : null;

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
  }

  // 27514 - Since E*Trade is unable to change the URL on their side, redirect to their landing page when they hit /term-insurance
  useEffect(() => {
    const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    if (props.location.pathname === '/final-expense' && account.account.accountName.toLowerCase().indexOf("etrade") !== -1) {
      props.history.push('/landing/etrade-term');
    }
  }, []);

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY);

  const prefill = props.location.search && props.location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();

  const newFormState = {
    currentStep: 1,
    pages: {
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  const [formState, setFormState] = useState(initialFormState);
  const [isSSOAuthenticated, setIsSSOAuthenticated] = useState(false);

  // When coming from the landing page form, skip to step 2
  const location = useLocation();
  useEffect(() => {
    if (location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
    if (!isSSOAuthenticated) {
      CheckSSOLogin().then((json) => {
        setIsSSOAuthenticated(json);
      });
    }
  }, []);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        window.location = '/final-expense';
      }
    }

    if (!nextStep) {
      return;
    }
    const newFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFormState);
  }, [path]);

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFormState);
    sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(newFormState));

    props.history.push(`/final-expense/${nextStep.path}`);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  formState.pages.quoter = {
    termLength: 0,
    coverageAmount: 20000,
    healthClass: 'PP',
    product: 'Final Expense Insurance',
  };

  const steps = [
    {
      step: 1,
      path: 'select-appointment',
      progressTitle: 'Schedule your application',
      title: "FINAL EXPENSE INSURANCE / SCHEDULE CALL",
      component: (
        <Steps.SelectAppointment
          formState={formState.pages.selectAppointment}
          quoter={formState.pages.quoter}
          data={EasyStepsSchedule}
          title="Easy Next Steps"
          sessionKey={SESSION_STORAGE_FINAL_EXPENSE_HEALTH_KEY}
          choiceConvenienceTitle="Choice and Convenience"
          choiceConvenienceData={ChoiceConvenienceData}
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('selectAppointment', field, value)}
          onNext={onNext}
          history={props.history}
          setMultiField={(firstName, lastName, email) => {
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                appointment: { ...formState.pages.appointment, firstName: firstName, lastName: lastName, email: email }
              }
            });
            sessionStorage.setItem(SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 2,
      path: 'thank-you',
      progressTitle: 'Schedule your application',
      title: "FINAL EXPENSE INSURANCE / CALL SCHEDULED",
      component: (
        <Steps.ScheduleAppointment
          quoter={formState.pages.quoter}
          appointment={formState.pages.selectAppointment}
          data={EasyStepsConfirmation}
          isFinalExpense={true}
          insurancePlan={InsurancePlan}
          title="What happens next"
          history={props.history}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },

  ];

  const currentStepObj = steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max.apply(Math, categories.map(function (o) {
    return o.categoryStep;
  }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);

  return (
    <div>
      <Helmet>
        <title>Final Expense Insurance - {currentStepObj.progressTitle} </title>
      </Helmet>

      <HeaderProgressTracker showCustomizeProgressTracker={true} title={currentStepObj.title}
                             hidden={currentStepObj.hidden} customClassNames={"customize-term"}
                             showContainer={currentStepObj.showContainer}
                             hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true}
                             stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3}
                             whiteBg={currentStepObj.whiteBackground} percent={percent}
                             fullWidth={currentStepObj.fullWidth}>
        <div className="finalExpense pb-3">
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}

FinalExpense.propTypes = {
  location: PropTypes.object,
  history: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/FinalExpense/FinalExpense.js